import { PersonAdd, Search } from '@mui/icons-material'
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import React, { RefObject } from 'react'
import { connect } from 'react-redux'
import { NivosModal } from 'components/index'
import NivosPaginator from 'components/nivos-paginator'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as MessageOperations,
  operations as messageOperations
} from 'states/dux/message/operations'
import { Payload as MessagePayload } from 'states/dux/message/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosContact } from 'types/nivos-message'
import { NivosPublicUser, NivosUserGroup } from 'types/user'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  message: MessagePayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    message: MessageOperations
  }
}

interface OwnProps {
  selectedContact?: NivosPublicUser | NivosUserGroup
  setSelectedContact: (contact: NivosPublicUser | NivosUserGroup) => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const MessageContactsFC: React.FC<any> = (props: AppProps) => {
  const { theme, user, operations, selectedContact, setSelectedContact } = props
  const [loading, setLoading] = React.useState(false)
  const [addContactOpen, setAddContactOpen] = React.useState(false)
  const [selectedAddContact, setSelectedAddContact] = React.useState<{
    label: string
    value: number
  }>()
  const [contactList, setContactList] = React.useState<NivosContact[]>([])
  const [groupChatList, setGroupChatList] = React.useState<NivosUserGroup[]>([])
  const [publicUserList, setPublicUserList] = React.useState<NivosPublicUser[]>(
    []
  )
  const [searchKey, setSearchKey] = React.useState('')
  const paginationContainerRef: RefObject<any> = React.createRef()

  const _filterData = (): void => {
    void get(`/message/contact/?search=${searchKey}`).then(data => {
      try {
        setContactList(
          data.map((contact: NivosPublicUser) =>
            objectifyDateStringFields(contact)
          )
        )
      } catch (e) {
        console.log(e)
      }
    })
  }

  const _addToContactList = (): void => {
    setLoading(true)
    if (selectedAddContact !== undefined) {
      void post('/message/contact/', { id: selectedAddContact.value }).then(
        (data: NivosPublicUser[]) => {
          const newList = data.map(item => objectifyDateStringFields(item))
          setContactList(newList)
          operations.user.update({ contactList: newList })
          setLoading(false)
          setAddContactOpen(false)
        }
      )
    } else {
      setLoading(false)
    }
  }

  const ContactListItem: React.FC<{
    data: NivosContact | NivosUserGroup
  }> = _props => {
    const { data } = _props

    const _isSelectedContact = (): boolean => {
      return (
        selectedContact?.id === data.id &&
        _getContactTitle(selectedContact) === _getContactTitle()
      )
    }

    const _getContactImageURL = (): string => {
      if ('image' in data) {
        return data.image !== undefined ? data.image.url : ''
      }
      return ''
    }

    const _getContactTitle = (contactData: any = null): string => {
      if (contactData == null) contactData = data
      if ('fullName' in contactData) {
        return contactData.fullName
      } else if ('name' in contactData) {
        return contactData.name
      }
      return ''
    }

    return (
      <Box
        bgcolor={_isSelectedContact() ? '#F3F3EE' : theme.primaryColor}
        borderColor={
          _isSelectedContact() ? theme.secondaryColor : 'transparent'
        }
        borderLeft={_isSelectedContact() ? 4 : 0}
        onClick={() => setSelectedContact(data)}
      >
        <Box
          display={'flex'}
          alignItems={'center'}
          padding={1}
          sx={{ cursor: 'pointer' }}
        >
          <Box padding={1}>
            <Avatar
              src={_getContactImageURL()}
              sx={{ width: 50, height: 50 }}
            />
          </Box>
          <Box flexGrow={1} height={'min-content'}>
            <Box display={'flex'}>
              <Typography flexGrow={1}>
                <strong>{_getContactTitle()}</strong>
              </Typography>
            </Box>
            <Typography variant={'body2'}>
              <em>
                {'lastMessage' in data ? data.lastMessage : 'Nivos Group Chat'}
              </em>
            </Typography>
          </Box>
        </Box>
        <Divider variant={'middle'} />
      </Box>
    )
  }

  const AddContactModal: React.FC<any> = _props => {
    return (
      <NivosModal
        title={'Add contact'}
        open={addContactOpen}
        setOpen={setAddContactOpen}
        containerProps={{ sx: { minHeight: 'fit-content', padding: 2 } }}
      >
        <Box paddingY={2}>
          <Autocomplete
            disablePortal
            options={publicUserList.map(pubUser => ({
              label: pubUser.fullName,
              value: pubUser.id
            }))}
            value={selectedAddContact}
            onChange={(_, value) => {
              if (value !== null) setSelectedAddContact(value)
            }}
            renderInput={params => (
              <TextField {...params} label="Contact name" />
            )}
          />
          <Box
            display={'flex'}
            justifyContent={'center'}
            paddingTop={2}
            width={1}
          >
            <Button
              disabled={loading}
              variant={'contained'}
              sx={{ marginLeft: 1 }}
              onClick={() => _addToContactList()}
            >
              {loading && (
                <CircularProgress size={12} sx={{ marginRight: 1 }} />
              )}
              Save
            </Button>
            <Button
              variant={'outlined'}
              sx={{ marginLeft: 1 }}
              onClick={() => setAddContactOpen(false)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </NivosModal>
    )
  }

  React.useEffect(() => {
    if (user.authenticated === true) {
      setGroupChatList(user.groups)
      setContactList(user.contactList)
    }
  }, [user])

  React.useEffect(() => {
    void get('/account/public_list/').then(data => {
      setPublicUserList(
        data.map((item: NivosPublicUser) => objectifyDateStringFields(item))
      )
    })
  }, [])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      width={0.9}
      height={1}
      marginLeft={5}
    >
      <AddContactModal />
      <Box>
        <Box display={'flex'} alignItems={'center'}>
          <Box flexGrow={1} height={'min-content'} marginY={'auto'} padding={2}>
            <Typography variant={'h4'} padding={2} paddingY={2}>
              Messages
            </Typography>
          </Box>
          <IconButton
            onClick={() => setAddContactOpen(true)}
            sx={{ height: 'fit-content', marginRight: 2 }}
          >
            <PersonAdd />
          </IconButton>
        </Box>
        <Box display={'flex'} paddingBottom={5}>
          <TextField
            fullWidth
            value={searchKey}
            onChange={event => setSearchKey(event.currentTarget.value)}
            placeholder={'Search contact name..'}
            onKeyDown={event => (event.key === 'Enter' ? _filterData() : null)}
            InputProps={{
              startAdornment: <Search />,
              sx: { borderRadius: '12px 0 0 12px' }
            }}
          />
        </Box>
      </Box>
      <Paper sx={{ borderRadius: '18px 0 0 18px', height: 0.45 }} elevation={8}>
        <Typography
          variant={'h5'}
          paddingTop={3}
          paddingLeft={3}
          paddingBottom={2}
        >
          Users
        </Typography>
        <Box ref={paginationContainerRef} height={1}>
          <NivosPaginator
            dataList={contactList}
            childElement={ContactListItem}
            itemsPerPage={5}
            adaptiveHeight={true}
            containerRef={paginationContainerRef}
          />
        </Box>
      </Paper>

      <Box paddingY={2} />

      <Paper
        sx={{
          borderRadius: '18px 0 0 18px',
          height: 0.45,
          display: 'flex',
          flexDirection: 'column',
          marginBottom: 1
        }}
        elevation={8}
      >
        <Typography
          variant={'h5'}
          paddingTop={3}
          paddingLeft={3}
          paddingBottom={2}
        >
          Groups
        </Typography>
        <Box flexGrow={1}>
          <NivosPaginator
            dataList={groupChatList}
            childElement={ContactListItem}
            itemsPerPage={5}
            adaptiveHeight={true}
            containerRef={paginationContainerRef}
          />
        </Box>
      </Paper>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    message: state.message
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  message: messageOperations
})

const MessageContacts = connect(
  mapStateToProps,
  mapDispatchToProps
)(MessageContactsFC) as React.FC<OwnProps>

export default MessageContacts
